import React, { useState, useRef } from 'react';
import { Label } from './ui/label';
import { Upload, X, Star, Plus, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const MultiImageUploader = ({ 
  images = [],
  setImages,
  maxImages = 7,
  label = 'Immagini',
  bucketName = 'images',
  mainImageIndex = 0,
  setMainImageIndex = null,
  showMainSelector = true
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Formato file non valido. Usa JPG, PNG o WebP');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File troppo grande. Massimo 5MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    if (images.length >= maxImages) {
      toast.error(`Massimo ${maxImages} immagini consentite`);
      event.target.value = '';
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${bucketName}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('rarifex-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('rarifex-uploads')
        .getPublicUrl(filePath);

      setImages([...images, publicUrl]);
      toast.success('Immagine caricata!');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Fallback: use FileReader for preview (base64)
      toast.info('Uso salvataggio locale.');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImages([...images, base64String]);
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    // Adjust main image index if needed
    if (setMainImageIndex) {
      if (index === mainImageIndex) {
        setMainImageIndex(0);
      } else if (index < mainImageIndex) {
        setMainImageIndex(mainImageIndex - 1);
      }
    }
  };

  const handleSetMain = (index) => {
    if (setMainImageIndex) {
      setMainImageIndex(index);
    }
  };

  const canAddMore = images.length < maxImages;

  return (
    <div className="space-y-3">
      <Label>{label} {maxImages > 1 && `(max ${maxImages})`}</Label>
      
      {/* Grid with images + upload button */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {/* Existing images */}
        {images.map((img, index) => (
          <div 
            key={index} 
            className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all ${
              mainImageIndex === index 
                ? 'border-[#DAA520] ring-2 ring-[#DAA520]/30' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <img 
              src={img} 
              alt={`Immagine ${index + 1}`} 
              className="w-full h-full object-cover"
            />
            
            {/* Remove button */}
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            >
              <X className="w-3 h-3" />
            </button>
            
            {/* Main image selector */}
            {showMainSelector && setMainImageIndex && (
              <button
                type="button"
                onClick={() => handleSetMain(index)}
                className={`absolute bottom-1 left-1 rounded-full p-1.5 transition-all ${
                  mainImageIndex === index 
                    ? 'bg-[#DAA520] text-white' 
                    : 'bg-white/90 text-slate-400 hover:text-[#DAA520]'
                }`}
                title={mainImageIndex === index ? 'Immagine principale' : 'Imposta come principale'}
              >
                <Star className={`w-3 h-3 ${mainImageIndex === index ? 'fill-current' : ''}`} />
              </button>
            )}
            
            {/* Image number badge */}
            <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
              {index + 1}
            </div>
          </div>
        ))}
        
        {/* Upload button - always visible if can add more */}
        {canAddMore && (
          <div 
            className={`aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
              uploading 
                ? 'border-[#008080] bg-[#008080]/5' 
                : 'border-slate-300 hover:border-[#008080] hover:bg-slate-50'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#008080] mb-2"></div>
                <span className="text-xs text-slate-500">Caricamento...</span>
              </div>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Plus className="w-5 h-5 text-slate-500" />
                </div>
                <span className="text-xs text-slate-500 text-center px-2">
                  {images.length === 0 ? 'Aggiungi immagine' : 'Aggiungi altra'}
                </span>
              </>
            )}
          </div>
        )}
        
        {/* Empty state placeholder slots */}
        {images.length === 0 && !uploading && (
          <>
            {[...Array(Math.min(2, maxImages - 1))].map((_, i) => (
              <div 
                key={`placeholder-${i}`}
                className="aspect-square border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center"
              >
                <ImageIcon className="w-6 h-6 text-slate-300" />
              </div>
            ))}
          </>
        )}
      </div>
      
      {/* Helper text */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>JPG, PNG o WebP (max 5MB)</span>
        <span>{images.length}/{maxImages} immagini</span>
      </div>
      
      {showMainSelector && images.length > 1 && (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Star className="w-3 h-3 text-[#DAA520]" />
          Clicca sulla stella per impostare l'immagine principale
        </p>
      )}
    </div>
  );
};
